<template>
  <WdsStack direction-column gap-lg>
    <WdsGrid :columns="2" gap-xs>
      <WdsStack direction-column gap-sm data-test="client-overview-location">
        <WdsText :sub-text="$t('Location')" />
        <!-- Staffing agencies never read a client's address, only its country. -->
        <LocationCell
          v-if="agencyLink"
          :country="agencyLink.clientCountry?.name ?? '—'"
          :country-code="agencyLink.clientCountry?.isoCode ?? null"
          :detail="client.client.region?.name ?? undefined"
          :title="$t('The country the client is registered in.')"
          data-test="client-overview-market"
        />
        <LocationDetails
          v-else
          :show-currency="false"
          :address="address"
          :region="client.client.region?.name ?? null"
          :location-tooltip="
            $t('The client\'s registered address, as it appears on contracts and invoices.')
          "
          :country-tooltip="$t('The country the client is registered in.')"
          currency=""
        />
        <WdsTooltip
          v-if="externalIdentifier && !agencyLink"
          global
          :z-index="50"
          placement="top"
          :text="$t('The client\'s identifier in your own systems.')"
        >
          <WdsStack align-center gap-sm>
            <WdsIcon
              class="tw-flex-shrink-0"
              :size="16"
              :name="WdsIconNameEnum.BUILDING_INSIGHTS_1"
            />

            <WdsText
              :text="externalIdentifier"
              :title="externalIdentifier"
              primary
              class="tw-line-clamp-1"
              data-test="client-overview-external-id"
            />
          </WdsStack>
        </WdsTooltip>
      </WdsStack>
      <WdsStack v-if="agencyLink" direction-column gap-sm data-test="client-overview-relation">
        <WdsText :sub-text="$t('Relation')" />
        <StaffingAgencyClientRelationLabel
          :is-direct="agencyLink.isDirect"
          :managing-company="agencyLink.managingCompany"
        />
      </WdsStack>
      <WdsStack v-else direction-column gap-sm>
        <WdsText :sub-text="$t('Contact person')" />
        <ClientContactLabel
          v-if="client.client.owner"
          sm
          :contact="client.client.owner"
          hide-avatar
          data-test="client-overview-contact"
        />
        <WdsText v-else secondary data-test="client-overview-contact">
          {{ $t('No contact person available') }}
        </WdsText>
      </WdsStack>
    </WdsGrid>

    <WdsGrid :columns="2" gap-xs>
      <WdsStack direction-column gap-xs data-test="client-overview-stat-placements">
        <WdsText secondary>{{ $t('Placements') }}</WdsText>

        <PlacementsCount
          :count="agencyLink?.listedHiresCount ?? client.hiresCount"
          :href="placementsUrl"
          data-test="client-overview-placements-link"
        />
      </WdsStack>
      <WdsStack direction-column gap-xs data-test="client-overview-stat-active">
        <WdsText secondary>{{ $t('Active placements') }}</WdsText>

        <PlacementsCount
          :count="client.activeHiresCount"
          :href="activePlacementsUrl"
          :empty-text="$t('No active placements')"
          data-test="client-overview-active-placements-link"
        />
      </WdsStack>
    </WdsGrid>
    <WdsGrid v-if="companyRelation" :columns="2" gap-xs>
      <WdsStack direction-column gap-xs data-test="client-overview-terms">
        <WdsText secondary>{{ $t('Payment terms') }}</WdsText>
        <div>
          <ClientPaymentTermsLabel
            :days="companyRelation.paymentTerm ?? null"
            :method="companyRelation.paymentTermMethod ?? null"
            :follows-client="companyRelation.paymentTermsFollowsClient"
            :z-index="50"
          />
        </div>
      </WdsStack>
      <WdsStack direction-column gap-xs data-test="client-overview-cadence">
        <WdsText secondary>{{ $t('Invoice cadence') }}</WdsText>
        <div>
          <ClientInvoiceCadenceLabel
            :cadence="companyRelation.invoiceBatchingCadence ?? null"
            :follows-client="companyRelation.invoiceBatchingCadenceFollowsClient"
            :z-index="50"
          />
        </div>
      </WdsStack>
    </WdsGrid>

    <WdsStack v-if="agencyLink && !agencyLink.isDirect" direction-column gap-md data-test="client-overview-msp-contacts">
      <WdsText :sub-text="$t(':msp team', { msp: agencyLink.managingCompany.name })" />
      <WdsStack v-if="agencyLink.managingCompanyContacts.length > 0" direction-column gap-sm>
        <ClientContactLabel
          v-for="(contact, index) in agencyLink.managingCompanyContacts"
          :key="contact.id"
          sm
          :contact="contact"
          :data-test="`client-overview-msp-contact-${index}`"
        />
      </WdsStack>
      <WdsText v-else secondary>{{ $t('No one from the MSP is assigned to this client yet') }}</WdsText>
    </WdsStack>

    <WdsStack v-if="companyRelation" direction-column gap-sm>
      <WdsStack direction-row align-center gap-sm>
        <WdsText :sub-text="$t('Linked staffing agencies')" />
        <WdsLabel xs data-test="client-overview-agencies-count">{{ linkedCount }}</WdsLabel>
      </WdsStack>
      <WdsStack v-if="linkedCount > 0" direction-column gap-sm>
        <WdsStack
          v-for="(linkedAgency, index) in visibleAgencies"
          :key="linkedAgency.id"
          direction-row
          align-center
          justify-space-between
          gap-sm
          :data-test="`client-overview-agency-row-${index}`"
        >
          <WdsText>{{ linkedAgency.recruiter.name }}</WdsText>
          <WdsLabel
            sm
            subtle
            :success="linkedAgency.status === ContactStatus.Active"
            :warning="linkedAgency.status === ContactStatus.Invited"
          >
            {{ statusLabel(linkedAgency.status) }}
          </WdsLabel>
        </WdsStack>
        <WdsText
          v-if="hiddenAgenciesCount > 0"
          secondary
          sm
          data-test="client-overview-agencies-more"
        >
          {{ $t('+ :count more', { count: hiddenAgenciesCount }) }}
        </WdsText>
      </WdsStack>
      <WdsText v-else secondary>{{ $t('No staffing agencies linked to this client yet') }}</WdsText>
      <div v-if="companyRelation.viewerCanManageStaffingAgencies" class="tw-flex">
        <WdsButtonGhost
          primary
          class="-tw-mx-1"
          data-test="client-overview-manage-agencies"
          @click="emit('manage-agencies')"
        >
          <WdsStack align-center gap-sm>
            <WdsIcon :name="WdsIconNameEnum.ADD" :size="18" />
            {{ linkedCount > 0 ? $t('Manage staffing agencies') : $t('Link staffing agencies') }}
          </WdsStack>
        </WdsButtonGhost>
      </div>
    </WdsStack>

    <WdsStack direction-column gap-sm data-test="client-overview-owners">
      <WdsStack direction-row align-center gap-sm>
        <WdsText :sub-text="$t('Client owners')" />
        <WdsLabel xs data-test="client-overview-owners-count">{{ owners.length }}</WdsLabel>
      </WdsStack>
      <WdsStack v-if="owners.length > 0" direction-column gap-sm>
        <WdsStack
          v-for="(owner, index) in owners"
          :key="owner.id"
          direction-row
          align-center
          justify-space-between
          gap-sm
          :data-test="`client-overview-owner-row-${index}`"
        >
          <WdsText>{{ owner.label }}</WdsText>
        </WdsStack>
      </WdsStack>
      <div v-if="client.viewerCanManageOwners" class="tw-flex">
        <WdsButtonGhost
          primary
          class="-tw-mx-1"
          data-test="client-overview-manage-owners"
          @click="emit('manage-owners')"
        >
          <WdsStack align-center gap-sm>
            <WdsIcon :name="WdsIconNameEnum.GROUP_ALT" :size="18" />
            {{ owners.length > 0 ? $t('Manage owners') : $t('Assign owners') }}
          </WdsStack>
        </WdsButtonGhost>
      </div>
    </WdsStack>

    <WdsStack v-if="organisations.length > 0" direction-column gap-md>
      <WdsText :sub-text="$t('Organisations')" />
      <WdsStack wrap gap-sm data-test="client-overview-organisations">
        <WdsLabel
          v-for="organisation in organisations"
          :key="organisation.id"
          :label="organisation.name"
          subtle
          md
        />
      </WdsStack>
    </WdsStack>

    <slot />

    <WdsStack direction-column gap-xs data-test="client-overview-added">
      <WdsText :sub-text="$t('Client added')" />
      <WdsText>{{ clientAdded }}</WdsText>
    </WdsStack>
  </WdsStack>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  WdsButtonGhost,
  WdsGrid,
  WdsIcon,
  WdsIconNameEnum,
  WdsLabel,
  WdsStack,
  WdsText,
  WdsTooltip,
} from '@worksome/wds-components'
import { FragmentType, graphql, useFragment } from '@/gql'
import { ContactStatus, HireActiveStatus } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { clientPlacementsUrl, placementScope } from '@/components/Clients/placementFilters'
import LocationDetails from '@/components/LocationDetails.vue'
import LocationCell from '@/components/LocationCell.vue'
import StaffingAgencyClientRelationLabel from '@/components/StaffingAgencyClients/StaffingAgencyClientRelationLabel.vue'
import PlacementsCount from '@/components/Clients/PlacementsCount.vue'
import ClientContactLabel from '@/components/Clients/ClientContactLabel.vue'
import useDate from '@/composables/useDate'
import ClientPaymentTermsLabel from '@/components/Clients/ClientPaymentTermsLabel.vue'
import ClientInvoiceCadenceLabel from '@/components/Clients/ClientInvoiceCadenceLabel.vue'
import { toOwnerSelection } from '@/components/Owners/fragments'

const AGENCY_LIST_LIMIT = 6

const ClientOverviewTabFields = graphql(`
  fragment ClientOverviewTab on ClientRelation {
    id
    createdAt
    hiresCount
    activeHiresCount: hiresCount(status: [ACTIVE])
    ... on Ownable {
      viewerCanManageOwners
      owners {
        id
        ...OwnerFields
      }
    }
    client {
      id
      name
      owner {
        id
        ...ClientContactLabel
      }
      address {
        formattedAddress(multiline: false)
        address
        city
        state
        postCode
        country {
          isoCode
          name
        }
      }
      region {
        id
        name
      }
      externalIdentifier
      organisations(first: 100) {
        data {
          id
          name
        }
      }
    }
    ... on CompanySupplier {
      viewerCanManageStaffingAgencies
      paymentTermsFollowsClient
      invoiceBatchingCadenceFollowsClient
      paymentTerm
      paymentTermMethod
      invoiceBatchingCadence {
        enabled
        intervalWeeks
        weekday
      }
      linkedStaffingAgencies(first: 10) {
        data {
          id
          status
          recruiter {
            id
            name
          }
        }
        paginatorInfo {
          total
        }
      }
    }
    ... on SupplierClient {
      isDirect
      clientCountry {
        isoCode
        name
      }
      # The agency's own placements list leaves drafts out; its table and drill-through count the same.
      listedHiresCount: hiresCount(status: [OFFERED, READY, ACTIVE, ENDED, CANCELLED, TERMINATED])
      managingCompany {
        id
        name
        avatar
      }
      managingCompanyContacts {
        id
        ...ClientContactLabel
      }
    }
  }
`)

const props = defineProps<{ client: FragmentType<typeof ClientOverviewTabFields> }>()

const emit = defineEmits<{ (e: 'manage-agencies'): void; (e: 'manage-owners'): void }>()

const client = computed(() => useFragment(ClientOverviewTabFields, props.client))

const companyRelation = computed(() => ('linkedStaffingAgencies' in client.value ? client.value : null))
const agencyLink = computed(() => ('managingCompany' in client.value ? client.value : null))

const linkedCount = computed(() => companyRelation.value?.linkedStaffingAgencies.paginatorInfo.total ?? 0)
const visibleAgencies = computed(
  () => companyRelation.value?.linkedStaffingAgencies.data.slice(0, AGENCY_LIST_LIMIT) ?? [],
)
const hiddenAgenciesCount = computed(() => Math.max(0, linkedCount.value - visibleAgencies.value.length))
const organisations = computed(() => client.value.client.organisations.data)
const owners = computed(() => ('owners' in client.value ? client.value.owners : []).map(toOwnerSelection))

const scope = computed(() => placementScope(client.value))

const placementsUrl = computed(() =>
  clientPlacementsUrl(scope.value.account, undefined, scope.value.endClient),
)

const activePlacementsUrl = computed(() =>
  clientPlacementsUrl(scope.value.account, [HireActiveStatus.Active], scope.value.endClient),
)

const address = computed(() => client.value.client.address ?? {})
const { date: clientAdded } = useDate(computed(() => client.value.createdAt))
const externalIdentifier = computed(() => client.value.client.externalIdentifier ?? '')


const statusLabels: Record<ContactStatus, string> = {
  [ContactStatus.Active]: $t('Active'),
  [ContactStatus.Applied]: $t('Applied'),
  [ContactStatus.Blocked]: $t('Blocked'),
  [ContactStatus.Declined]: $t('Declined'),
  [ContactStatus.Invited]: $t('Invited'),
}

function statusLabel(status: ContactStatus): string {
  return statusLabels[status] ?? status
}
</script>
